import { UserName } from "../redux/reducers/userReducer";

const BASE_DEMO_MEDIA_URL = "https://demo.persona-ai.ai/v1/media";
const BASE_STAGING_MEDIA_URL = "https://staging.persona-ai.ai/v1/media";

export const getFullNameFromUserName = (name?: UserName): string => {
  if (!name) return "";

  const { first_name = "", middle_name = "", last_name = "" } = name;
  return [first_name, middle_name, last_name].filter(Boolean).join(" ");
};

export const getFullPronunciationFromUserName = (name?: UserName): string => {
  if (!name) return "";

  const {
    first_name_phonetic = "",
    middle_name_phonetic = "",
    last_name_phonetic = "",
  } = name;
  return [first_name_phonetic, middle_name_phonetic, last_name_phonetic]
    .filter(Boolean)
    .join(" ");
};

export const getTargetId = (
  messageId: number,
  chatSessionId: string
): string => {
  return `msg:${chatSessionId}:${messageId.toString()}`;
};

export const getMediaBaseUrl = () => {
  // For example, if window.location.hostname is "localhost"
  // use the staging endpoint. Otherwise, use the demo endpoint.
  if (window.location.hostname === "localhost") {
    return BASE_STAGING_MEDIA_URL;
  }
  return BASE_DEMO_MEDIA_URL;
};

export const getNestedValue = (
  obj: any,
  path: string,
  defaultValue: any = null
) => {
  return (
    path.split(".").reduce((acc, part) => acc?.[part], obj) ?? defaultValue
  );
};

/**
 * Generate a user-friendly display name with ID in parentheses
 * @param displayName - The primary display name (e.g., bot_name, display_title)
 * @param id - The resource ID
 * @returns Formatted string like "Name (id)" or just "id" if no display name
 */
export const getDisplayNameWithId = (
  displayName: string | null | undefined,
  id: string
): string => {
  if (displayName && displayName.trim()) {
    return `${displayName} (${id})`;
  }
  return id;
};

/**
 * Get agent config display name
 * @param agentConfig - Agent config object with display_name, bot_name and id
 * @returns Formatted display name showing both title and name when available
 */
export const getAgentConfigDisplayName = (agentConfig: {
  display_name?: string | null | undefined;
  bot_name?: string | null | undefined;
  id: string;
}): string => {
  const displayName = agentConfig.display_name?.trim();
  const botName = agentConfig.bot_name?.trim();
  
  // If we have both display_name (title) and bot_name (name), show both
  if (displayName && botName) {
    return `${displayName} - ${botName} (${agentConfig.id})`;
  }
  
  // If we only have one, show that with ID
  const name = displayName || botName;
  return getDisplayNameWithId(name, agentConfig.id);
};

/**
 * Get assessment display name
 * @param assessment - Assessment object with display_title and id
 * @returns Formatted display name
 */
export const getAssessmentDisplayName = (assessment: {
  display_title?: string | null;
  id: string;
}): string => {
  return getDisplayNameWithId(assessment.display_title, assessment.id);
};

/**
 * Get roleplay display name
 * @param roleplay - Roleplay object with display_title and id
 * @returns Formatted display name
 */
export const getRoleplayDisplayName = (roleplay: {
  display_title?: string | null;
  id: string;
}): string => {
  return getDisplayNameWithId(roleplay.display_title, roleplay.id);
};

/**
 * Get organization display name
 * @param organization - Organization object with name and id
 * @returns Formatted display name
 */
export const getOrganizationDisplayName = (organization: {
  name?: string;
  id: string;
}): string => {
  return getDisplayNameWithId(organization.name, organization.id);
};
